import dynamic from 'next/dynamic'
import Image, { ImageLoader, ImageProps } from 'next/image'
import { useMemo, useRef, useState, useContext } from 'react'
import SvgPerson from '#/assets/images/icons/person_marker.svg'
import SvgImage from '#/assets/images/propertyDetail/default-large.svg'
import ViewModeContext from '#/contexts/ViewMode'
import { CarouselItem } from '../common'

const Gallery = dynamic(() => import('./Gallery'), { ssr: false })

type Props = {
  firstShownCount?: number
  isBot?: boolean
  media?: TopHap.PropertyMedia
  property: TopHap.Property
  openStreetView: () => void
}
const LISTHUB_IMAGE_CDN = 'https://lh.rdcpix.com'

const ImageContainer: React.FC<ImageProps> = ({
  alt,
  loader,
  priority,
  src,
  unoptimized
}) => {
  const [imgLoaded, setImgLoaded] = useState(false)
  const onLoaded = () => {
    setImgLoaded(true)
  }
  const viewMode = useContext(ViewModeContext)

  return (
    <>
      {(!imgLoaded && viewMode === 'WEB_DETAIL_PAGE') && (
        <div className='absolute h-full w-full overflow-hidden rounded-xl'>
          <svg
            fill='none'
            height='216'
            viewBox='0 0 320 216'
            width='320'
            xmlns='http://www.w3.org/2000/svg'
          >
            <rect fill='#F2F2F2' height='216' width='320' />
          </svg>
        </div>
      )}
      <Image
        alt={alt}
        className='h-[216px] w-80 object-cover'
        height={216}
        loader={loader}
        priority={priority}
        src={src}
        unoptimized={unoptimized}
        width={320}
        onLoadingComplete={() => onLoaded()}
      />
    </>
  )
}

const Images: React.FC<Props> = ({
  isBot,
  firstShownCount = 1,
  media,
  property,
  openStreetView
}) => {
  const [selected, setSelected] = useState(-1)
  const streetImage = useMemo(
    () => (!isBot && property.TophapStatus !== 'Active' && property.TophapStatus !== 'Pending' ?
      'StreetImage' : undefined),
    [property]
  )
  const images = useMemo(() => {
    const photos = [...media?.photos || []]
    if (streetImage) {
      photos.splice(1, 0, streetImage)
      return photos
    } else {
      return photos
    }
  }, [media, property, streetImage])
  const streetImageIndex = media?.photos.length ? 1 : 0
  const refContainer = useRef<HTMLDivElement>(null)

  const handleItemClick = (index: number) => {
    if (streetImage && index === streetImageIndex) {
      openStreetView()
    } else {
      setSelected((streetImage && index > streetImageIndex) ? index - 1 : index)
    }
  }

  const listhubImageLoader: ImageLoader = ({ src }) => src.replace('r.', 'm.')

  if (!images.length) return null

  return (
    <>
      <div ref={refContainer} className='block snap-x snap-mandatory space-x-3 overflow-x-auto overscroll-x-none whitespace-nowrap px-4 pb-2'>
        {images.map((e, index) => (
          <CarouselItem
            key={index}
            className='inline-flex cursor-pointer overflow-hidden'
            onClick={() => handleItemClick(index)}
          >

            {(!!streetImage && index === streetImageIndex) ? (
              <div className='relative h-[216px] w-80'>
                <SvgImage className='full' />
                <div className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 space-x-1 rounded-full bg-primary px-4 py-2 text-white'>
                  <SvgPerson className='h-6 w-6' />
                  <span className='text-sm'>Street View</span>
                </div>
              </div>
            ) : (
              <ImageContainer
                alt={`Photo ${index}`}
                loader={e.startsWith(LISTHUB_IMAGE_CDN) ? listhubImageLoader : undefined}
                priority={index < firstShownCount}
                src={e}
                unoptimized={!!streetImage && index === streetImageIndex}
              />
            )}
          </CarouselItem>
        ))}
      </div>
      {selected !== -1 && (
        <Gallery
          images={media?.photos || []}
          index={selected}
          property={property}
          onClose={() => setSelected(-1)}
        />
      )}
    </>
  )
}

export default Images
